import { useDisclosure } from "@mantine/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "src/components/shared/Button/Button";
import { type IReportEntry } from "src/pages/Reports/Report/Report.types";
import { useSelector } from "src/redux/hooks";
import { type ILogbookCharts } from "src/redux/slices/entriesGeneratorSlice";

import {
  buildChartData as buildData,
  hasChartSelection,
} from "./buildChartData";
import { mergeChartSettings } from "./CreateChartButton.utils";
import CreateChartModal from "./CreateChartModal";

interface Props {
  entries: IReportEntry[];
}

const CreateChartButton = ({ entries }: Props) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [data, setData] = useState<ILogbookCharts[]>([]);
  const [loading, setLoading] = useState(false);
  const {
    selectedCols,
    selectedCells,
    sections,
    chartsData,
    chartsIncludeUnverified,
  } = useSelector(state => state.entriesGeneratorSlice);
  const [includeUnverified, setIncludeUnverified] = useState(
    chartsIncludeUnverified,
  );
  useEffect(() => {
    setIncludeUnverified(chartsIncludeUnverified);
  }, [chartsIncludeUnverified]);

  const buildChartData = useCallback(
    (withUnverified: boolean) =>
      buildData({
        entries,
        sections,
        selectedCols,
        selectedCells,
        withUnverified,
      }),
    [entries, sections, selectedCells, selectedCols],
  );

  const handleCreateChart = useCallback(() => {
    // Defer the heavy traversal to the next tick so the button can paint
    // its loading state first, instead of the click freezing with no
    // visual feedback while the computation runs.
    setLoading(true);
    setTimeout(() => {
      setData(
        mergeChartSettings(buildChartData(includeUnverified), chartsData),
      );
      setLoading(false);
      open();
    }, 0);
  }, [buildChartData, chartsData, includeUnverified, open]);

  const handleIncludeUnverifiedChange = useCallback(
    (checked: boolean) => {
      setIncludeUnverified(checked);
      setData(prev => mergeChartSettings(buildChartData(checked), prev));
    },
    [buildChartData],
  );

  const disabled = useMemo(
    () => !hasChartSelection(selectedCols, selectedCells),
    [selectedCells, selectedCols],
  );

  return (
    <>
      <Button disabled={disabled} loading={loading} onClick={handleCreateChart}>
        {chartsData.length > 0 ? "Edit" : "Create"}
      </Button>
      <CreateChartModal
        opened={opened}
        close={close}
        data={data}
        setData={setData}
        includeUnverified={includeUnverified}
        onIncludeUnverifiedChange={handleIncludeUnverifiedChange}
      />
    </>
  );
};

export default CreateChartButton;
